import * as React from 'react';
import {useEffect, useState} from 'react';
import {IonIcon} from '@ionic/react';
import {
    chevronBackOutline,
    chevronForwardOutline,
    closeOutline,
    contractOutline,
    expandOutline
} from 'ionicons/icons';

import {Camera} from '../../interfaces/Camera';
import ReactPlayerModule from 'react-player';
import {cjsDefault} from '../../shims/cjs-default';
import './CctvOverlayPanel.css';

const ReactPlayer = cjsDefault(ReactPlayerModule);

interface CctvOverlayPanelProps {
    cameras: Camera[];
    onRemove: (id: number) => void;
    onCloseAll: () => void;
    onError: (error: string, id: number) => void;
    className?: string;
}

/**
 * The CCTV panel from TITAN, rebuilt on this app's tokens: a translucent
 * dark panel docked to the map's right edge, opening itself when a camera is
 * picked, with a chevron tab to bring it back and a full-width mode for
 * watching several streams side by side.
 */
const CctvOverlayPanel: React.FC<CctvOverlayPanelProps> = (props: CctvOverlayPanelProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isWide, setIsWide] = useState(false);

    const count = props.cameras.length;

    // Picking a camera on the map should bring the panel back into view.
    useEffect(() => {
        if (count > 0) {
            setIsOpen(true);
        }
    }, [count]);

    return (
        <>
            {!isWide && (
                <button
                    type="button"
                    className={`cctv-panel-tab ${isOpen ? 'cctv-panel-tab--open' : 'cctv-panel-tab--closed'}`}
                    aria-label={isOpen ? 'Collapse CCTV panel' : 'Show CCTV panel'}
                    onClick={() => setIsOpen(open => !open)}
                >
                    <IonIcon icon={isOpen ? chevronForwardOutline : chevronBackOutline} />
                    {/* So a hidden panel still says how many feeds are open. */}
                    {!isOpen && count > 0 && <span className="cctv-panel-tab__count">{count}</span>}
                </button>
            )}

            <aside
                className={`cctv-panel${isOpen ? ' cctv-panel--open' : ''}${isWide ? ' cctv-panel--wide' : ''}${props.className ? ` ${props.className}` : ''}`}
                aria-hidden={!isOpen}
            >
                <header className="cctv-panel__header">
                    <h2 className="cctv-panel__title">Live cameras</h2>
                    <div className="cctv-panel__controls">
                        <button
                            type="button"
                            className="cctv-panel__btn"
                            aria-label={isWide ? 'Collapse panel' : 'Expand panel'}
                            onClick={() => setIsWide(wide => !wide)}
                        >
                            <IonIcon icon={isWide ? contractOutline : expandOutline} />
                        </button>
                        <button
                            type="button"
                            className="cctv-panel__btn cctv-panel__btn--danger"
                            aria-label="Close all cameras"
                            title="Close all cameras"
                            onClick={() => {
                                // X shuts the feeds down, it does not just
                                // tuck them out of sight; the chevron tab is
                                // what collapses the panel.
                                props.onCloseAll();
                                setIsOpen(false);
                                setIsWide(false);
                            }}
                        >
                            <IonIcon icon={closeOutline} />
                        </button>
                    </div>
                </header>

                {isOpen && count > 0 && (
                    <div className="cctv-panel__toolbar">
                        <button type="button" className="cctv-panel__close-all" onClick={props.onCloseAll}>
                            Close all {count} camera{count === 1 ? '' : 's'}
                        </button>
                    </div>
                )}

                <div className="cctv-panel__body">
                    {/* Hidden means stopped: leaving the players mounted kept
                        every stream running behind the closed panel. */}
                    {!isOpen ? null : count === 0 ? (
                        <p className="cctv-panel__empty">No camera selected</p>
                    ) : (
                        props.cameras.map(camera => (
                            <div className="cctv-card" key={camera.id}>
                                <span className="cctv-card__title">
                                    {camera.description || 'No description'} (Video)
                                </span>
                                <div className="cctv-card__stream">
                                    <ReactPlayer
                                        width="100%"
                                        height="100%"
                                        url={camera.https_url}
                                        onError={(error: string) => props.onError(error, camera.id)}
                                        playing={true}
                                        volume={1}
                                        muted={true}
                                        controls={true}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="cctv-card__close"
                                    onClick={() => props.onRemove(camera.id)}
                                >
                                    Close
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </aside>
        </>
    );
};

export default CctvOverlayPanel;
